import type { FunctionComponent } from "react";
import ReactJson from "react-json-view";
import Profile from "components/shared/Profile";
import useLeadDetails from "./useLeadDetails";
import Address from "components/shared/Address";
import { formatAddress, formatAddressAsString } from "./helpers";
import { Card, ExpandableContent, Price } from "@kanda-libs/ks-design-library";
import LeadActions from "../LeadActions";
import Link from "components/shared/Link";
import { URLS } from "components/shared/App/constants";
import { capitaliseWords } from "common/helpers";

const LeadDetails: FunctionComponent = function ({}) {
  const { lead, company, jobs } = useLeadDetails();

  const jobLink =
    lead?.status === "open" &&
    `https://apply-qa.kanda.co.uk/apply/job/${lead.id}`;

  const staffJobPath = `${process.env.REACT_APP_HOME_URL}job/`;

  return (
    <Card>
      <>
        <div className="grid grid-cols-2 gap-x-2">
          <div>
            <Profile.Section title="Customer details">
              <Profile.Property
                label="Name"
                value={[
                  lead?.lead_applicant?.customer_details?.first_name,
                  lead?.lead_applicant?.customer_details?.last_name,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
              <Profile.Property
                label="Email"
                value={lead?.lead_applicant?.customer_details?.email}
              />
              <Profile.Property
                label="Current address"
                value={<Address address={formatAddress(lead)} />}
                valueToCopy={formatAddressAsString(lead)}
              />
            </Profile.Section>
            <Profile.Section title="Lead details">
              <Profile.Property label="Status" value={lead?.status} />
              <Profile.Property
                label="Trade"
                value={
                  <Link to={URLS.company.replace(":id", company?.id || "")}>
                    <>{company?.contact_info?.trading_name || ""}</>
                  </Link>
                }
              />
              <Profile.Property
                label="Max credit line"
                value={<Price amount={lead?.max_credit_amount?.amount} />}
              />
              {jobLink ? (
                <Profile.Property
                  label="Post job link"
                  value={<div className="text-left">{jobLink}</div>}
                  valueToCopy={jobLink}
                />
              ) : (
                <></>
              )}
            </Profile.Section>
            {lead?.lead_quote && (
              <Profile.Section title="Lead quote">
                <Profile.Property
                  label="Description"
                  value={lead.lead_quote.description}
                />
                <Profile.Property
                  label="Work type"
                  value={capitaliseWords(
                    lead.lead_quote.work_type.replace(/_/g, " ")
                  )}
                />
                <Profile.Property
                  label="Work finished by"
                  value={capitaliseWords(lead.lead_quote.work_finished_by)}
                />
                <Profile.Property
                  label="VAT"
                  value={
                    <Price amount={lead.lead_quote.total?.amount_vat?.amount} />
                  }
                />
                <Profile.Property
                  label="Total exc VAT"
                  value={
                    <Price
                      amount={
                        lead.lead_quote.total?.amount_vat_exclusive?.amount
                      }
                    />
                  }
                />
                <Profile.Property
                  label="Total inc VAT"
                  value={
                    <Price
                      amount={
                        lead.lead_quote.total?.amount_vat_inclusive?.amount
                      }
                    />
                  }
                />
              </Profile.Section>
            )}
            <Profile.Section
              title={`Job details - "${lead?.source}" flow via "${
                lead?.referrer || "n/a"
              }"`}
            >
              {jobs.map((job) => (
                <Profile.Property
                  label={
                    lead?.source === "checkout"
                      ? "Checkout Job"
                      : "Referred Job"
                  }
                  value={
                    <div className="text-left">
                      <a
                        href={`${staffJobPath}${job?.id}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <>
                          ID: {job?.id}
                          <hr></hr>
                          {job?.archived == "yes" ? "[Archived] " : ""}
                          Status: {job?.status} + {job?.finance_status}
                        </>
                      </a>
                    </div>
                  }
                  clickToCopy={false}
                />
              ))}
            </Profile.Section>
          </div>
          <div>
            <LeadActions />
          </div>
        </div>
        {lead && (
          <Profile.Section title="Quote details">
            <ExpandableContent
              showLabel="Show quote json"
              hideLabel="Hide quote json"
            >
              <ReactJson src={lead} />
            </ExpandableContent>
          </Profile.Section>
        )}
      </>
    </Card>
  );
};

export default LeadDetails;
